<template>
  <LayoutOverlay @closeOverlay="emits('closeSearchOverlay')">
    <ModalTop class="layout-search__modal">
      <AccordionText
        :accordionText="t('general__text__categories')"
        class="layout-search__accordion"
        :active="isCategoriesExpanded"
        :size="'medium'"
        @onClicked="isCategoriesExpanded = !isCategoriesExpanded"
      />
      <div v-show="isCategoriesExpanded" class="layout-search__category-select">
        <DropdownFilterComplex
          :complexes="allComplexes"
          :selectedBuilding="selectedBuilding?.properties"
          :selectedRealEstate="selectedRealEstate?.properties"
          @complex="setComplexFromDropdown"
        />
        <DropdownFilterRealEstate
          :realEstates="filteredRealEstates"
          :selectedBuilding="selectedBuilding?.properties"
          :selectedComplex="selectedComplex?.properties"
          @realEstate="setRealEstateFromDropdown"
        />
        <DropdownFilterBuilding
          :buildings="filteredBuildings"
          :selectedBuilding="selectedBuilding?.properties"
          :selectedRealEstate="selectedRealEstate?.properties"
          :selectedComplex="selectedComplex?.properties"
          @building="setBuildingFromDropdown"
        />
        <ButtonPrimary :buttonText="t('general__text__select')" @onClicked="onSelect" />
      </div>
      <hr class="layout-search__divider" />
      <LayoutSearchResults
        :searchResults="searchResults"
        :isIndoorviewShowing="isIndoorviewShowing"
        :isTyping="isTyping"
        :isLoading="isLoading"
        :recentSearch="recentSearch"
        :searchString="searchString"
        :mobileScreen="mobileScreen"
        @selected-building="findAndSelectBuildingResult"
        @selected-complex="findAndSelectComplexResult"
        @selected-real-estate="findAndSelectRealEstateResult"
        @selected-room="findAndSelectRoom"
      />
    </ModalTop>
  </LayoutOverlay>
</template>

<script lang="ts" setup>
// Imports
import { computed, onMounted, ref, toRefs, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import AccordionText from '@/design-system/atoms/AccordionText.vue'
import ButtonPrimary from '@/design-system/atoms/buttons/ButtonPrimary.vue'
import DropdownFilterBuilding from '@/design-system/molecules/DropdownFilterBuilding.vue'
import DropdownFilterComplex from '@/design-system/molecules/DropdownFilterComplex.vue'
import DropdownFilterRealEstate from '@/design-system/molecules/DropdownFilterRealEstate.vue'
import LayoutOverlay from '@/design-system/templates/LayoutOverlay.vue'
import ModalTop from '@/design-system/templates/ModalTop.vue'
import { BUILDING } from '@/interfaces/building.interface'
import { COMPLEX } from '@/interfaces/complex.interface'
import { REAL_ESTATE } from '@/interfaces/realEstate.interface'
import { setViewToMapview, setViewToPlanview } from '@/utils/views'
import LayoutSearchResults from '@/design-system/organisms/LayoutSearchResults.vue'
import { search } from '@/services/search.service'
import { SearchObject, ResultObject } from '@/interfaces/search.interface'
import { Room } from '@/interfaces/room.interface'
import { LEVEL } from '@/interfaces/level.interface'
import { debounce } from '@/utils/debounce'
import { zoomToEntities } from '@/composables/useMapbox'
import { useSelectionStore } from '@/pinia/selectionNew'
// Props (defineProps)
const props = defineProps({
  searchString: {
    type: String,
    default: ''
  },
  isTyping: {
    type: Boolean,
    default: false
  }
})
const { searchString, isTyping } = toRefs(props)
// Emits (defineEmits)
interface EMITS {
  (e: 'closeSearchOverlay')
  (e: 'clearSearch')
}
const emits = defineEmits<EMITS>()
// Reactive variables (incl. composables)
const { t } = useI18n()
const isCategoriesExpanded = ref(false)
const isLoading = ref(false)
const recentSearch = ref<ResultObject[]>([])
const results = ref<{
  complexes: SearchObject[]
  realEstates: SearchObject[]
  buildings: SearchObject[]
  rooms: SearchObject[]
}>({
  complexes: [],
  realEstates: [],
  buildings: [],
  rooms: []
})
const selectedComplex = ref<COMPLEX>()
const selectedRealEstate = ref<REAL_ESTATE>()
const selectedBuilding = ref<BUILDING>()
const selectedRoom = ref<Room>()
const store = useStore()
const selection = useSelectionStore()
// Computed – (possibly divide into two: “Computed (store)” and “Computed (component specific)”?)
const allBuildings = computed<BUILDING[]>(() => store.getters.allBuildings)
const allComplexes = computed<COMPLEX[]>(() => store.getters.allComplexes)
const allRealEstates = computed<REAL_ESTATE[]>(() => store.getters.allRealEstates)
const isIndoorviewShowing = computed<boolean>(() => store.getters.isIndoorviewShowing)
const mobileScreen = computed<boolean>(() => store.state.mobileScreen)
const roomsForSelectedLevel = computed(() => store.state.roomsForSelectedLevel)
const levelsForSelectedBuilding = computed(() => store.state.levelsForSelectedBuilding)

const filteredBuildings = computed(() => {
  if (selectedRealEstate.value)
    return allBuildings.value.filter(
      (building) =>
        building.properties.realEstateId === selectedRealEstate.value?.properties.belongsToId
    )
  else if (selectedComplex.value)
    return allBuildings.value.filter(
      (building) => building.properties.complexId === selectedComplex.value?.properties.belongsToId
    )
  return allBuildings.value
})
const filteredRealEstates = computed(() => {
  if (selectedComplex.value)
    return allRealEstates.value.filter(
      (realEstate) =>
        realEstate.properties.complexId === selectedComplex.value?.properties.belongsToId
    )
  return allRealEstates.value
})
const formattedData = computed(() => {
  const complexes = results.value.complexes.map((complex) => {
    return { id: complex.reference.id, header: complex.displayName, type: 'COMPLEX' }
  })
  const realEstates = results.value.realEstates.map((realEstate) => {
    let complex = allComplexes.value.find(
      (complex) => complex.id === findParent('COMPLEX', realEstate)?.id
    )
    return {
      id: realEstate.reference.id,
      header: realEstate.displayName,
      subheader: complex?.properties.displayName,
      type: 'REAL_ESTATE'
    }
  })
  const buildings = results.value.buildings.map((building) => {
    let realEstate = allRealEstates.value.find(
      (realEstate) => realEstate.id === findParent('REAL_ESTATE', building)?.id
    )
    let complex = allComplexes.value.find(
      (complex) => complex.id === findParent('COMPLEX', building)?.id
    )
    let subheader = ''
    if (realEstate && complex) {
      subheader = `${realEstate.properties.displayName}, ${complex?.properties.displayName}`
    } else if (realEstate) {
      subheader = `${realEstate.properties.displayName}`
    }
    return {
      id: building.reference.id,
      header: building.displayName,
      subheader: subheader,
      type: 'BUILDING'
    }
  })
  const rooms = results.value.rooms.map((room) => {
    let building = allBuildings?.value.find(
      (building) => building.id === findParent('CONSTRUCTION', room)?.id
    )
    let realEstate = allRealEstates?.value.find(
      (realEstate) => realEstate.id === building?.properties.realEstateId
    )
    let complex = allComplexes?.value.find(
      (complex) => complex.id === building?.properties.complexId
    )
    let subheader = `${building ? building?.properties.displayName : ''}${
      realEstate ? ', ' + realEstate.properties.displayName : ''
    }${complex ? ', ' + complex.properties.displayName : ''}`
    return {
      id: room.reference.id,
      header: room.displayName,
      subheader: subheader,
      levelId: findParent('LEVEL', room)?.id,
      buildingId: findParent('CONSTRUCTION', room)?.id,
      type: 'ROOM'
    }
  })
  return { complexes, realEstates, buildings, rooms }
})
const searchResults = computed(() => {
  if (searchString.value.length < 1)
    return {
      complexes: [],
      buildings: [],
      realEstates: [],
      rooms: [],
      pois: []
    }
  else
    return {
      complexes: formattedData.value.complexes,
      realEstates: formattedData.value.realEstates,
      buildings: formattedData.value.buildings,
      rooms: formattedData.value.rooms,
      pois: []
    }
})
// Methods
function addRecentSearch(value: ResultObject) {
  if (recentSearch.value.find((recentItem) => recentItem.id === value.id)) {
    const foundIndex = recentSearch.value.findIndex((recentItem) => recentItem.id === value.id)
    recentSearch.value.splice(foundIndex, 1)
  }
  if (recentSearch.value.length > 4) recentSearch.value.pop()
  recentSearch.value.unshift({
    id: value.id,
    header: value.header,
    subheader: value.subheader,
    levelId: value.levelId ? value.levelId : '',
    buildingId: value.buildingId ? value.buildingId : '',
    type: value.type
  })
  localStorage.setItem('recentSearches', JSON.stringify(recentSearch.value))
}
function commitClick(type: string) {
  store.commit('SET_CLICKED_ITEM_TYPE', { type: type })
}
const fetchAndSortData = debounce(async (searchString: string) => {
  let res = await search(searchString)
  results.value = {
    complexes: [],
    realEstates: [],
    buildings: [],
    rooms: []
  }
  res.forEach(async (item: SearchObject) => {
    if (item.type === 'COMPLEX') results.value.complexes.push(item)
    else if (item.type === 'REAL_ESTATE') results.value.realEstates.push(item)
    else if (item.type === 'CONSTRUCTION') results.value.buildings.push(item)
    else {
      const roomHeaderAddition = item.properties?.nameTenant?.length
        ? ' (' + item.properties?.nameTenant + ')'
        : ''
      item.displayName = item.displayName + roomHeaderAddition
      results.value.rooms.push(item)
    }
  })
  isLoading.value = false
}, 400)
async function onSelect() {
  if (selectedRoom.value) {
    await store.dispatch('CHANGE_ROOM', { roomId: selectedRoom.value.id })
    commitClick('room')
    setViewToPlanview()
    zoomToEntities([selectedRoom.value.id])
    const relatedBuilding = allBuildings.value.find(
      (building) => building.id === selectedRoom.value?.properties.constructionId
    )
    addRecentSearch({
      id: selectedRoom.value.id,
      header: selectedRoom.value.properties.displayName,
      subheader: relatedBuilding?.properties.displayName,
      levelId: selectedRoom.value.properties.level,
      buildingId: relatedBuilding?.properties.id,
      type: 'ROOM'
    })
  } else if (selectedBuilding.value) {
    await store.dispatch('CHANGE_BUILDING', { building: selectedBuilding.value })
    commitClick('building')
    setViewToMapview()
    setBuildingFromDropdown(selectedBuilding.value)
    const relatedRealEstate = allRealEstates.value.find(
      (realEstate) => realEstate.id === selectedBuilding.value?.properties.realEstateId
    )
    addRecentSearch({
      id: selectedBuilding.value.id,
      header: selectedBuilding.value.properties.displayName,
      subheader: relatedRealEstate?.properties.displayName,
      type: 'BUILDING'
    })
    zoomToEntities([selectedBuilding.value.id])
  } else if (selectedRealEstate.value) {
    await store.dispatch('CHANGE_REAL_ESTATE', { realEstate: selectedRealEstate.value.properties })
    commitClick('realEstate')
    setViewToMapview()
    setRealEstateFromDropdown(selectedRealEstate.value)
    const relatedComplex = allComplexes.value.find(
      (complex) => complex.id === selectedRealEstate.value?.properties.complexId
    )
    addRecentSearch({
      id: selectedRealEstate.value.id,
      header: selectedRealEstate.value.properties.displayName,
      subheader: relatedComplex?.properties.displayName,
      type: 'REAL_ESTATE'
    })
    zoomToEntities([selectedRealEstate.value.id])
  } else if (selectedComplex.value) {
    await store.dispatch('CHANGE_COMPLEX', { complex: selectedComplex.value.properties })
    commitClick('complex')
    setViewToMapview()
    setComplexFromDropdown(selectedComplex.value)
    addRecentSearch({
      id: selectedComplex.value.id,
      header: selectedComplex.value.properties.displayName,
      subheader: '',
      type: 'COMPLEX'
    })
    zoomToEntities([selectedComplex.value.id])
  }
  selectedComplex.value = undefined
  selectedRealEstate.value = undefined
  selectedBuilding.value = undefined
  selectedRoom.value = undefined
  emits('closeSearchOverlay')
  emits('clearSearch')
}
function setBuilding(value: BUILDING) {
  selectedBuilding.value = value
}
function setBuildingFromDropdown(value: BUILDING) {
  selection.set([value.id])
  const relatedComplex = allComplexes.value.find(
    (complex) => complex.id === value.properties.complexId
  )
  const relatedRealEstate = allRealEstates.value.find(
    (realEstate) => realEstate.id === value.properties.realEstateId
  )
  let subheader = ''
  if (relatedRealEstate && relatedComplex) {
    subheader = `${relatedRealEstate.properties.displayName}, ${relatedComplex?.properties.displayName}`
  } else if (relatedRealEstate) {
    subheader = `${relatedRealEstate.properties.displayName}`
  }
  findAndSelectBuilding({
    id: value.id,
    header: value.properties.displayName,
    subheader: subheader,
    type: 'BUILDING'
  })
}
function setComplex(value: COMPLEX) {
  selectedRealEstate.value = undefined
  selectedBuilding.value = undefined
  selectedComplex.value = value
}
function setComplexFromDropdown(value: COMPLEX) {
  selection.set([value.id])
  findAndSelectComplex({
    id: value.id,
    header: value.properties.displayName,
    type: 'COMPLEX'
  })
}
function setRealEstate(value: REAL_ESTATE) {
  selectedBuilding.value = undefined
  selectedRealEstate.value = value
}
function setRealEstateFromDropdown(value: REAL_ESTATE) {
  selection.set([value.id])
  const relatedComplex = allComplexes.value.find(
    (complex) => complex.id === value.properties.complexId
  )
  const subheader = relatedComplex ? relatedComplex.properties.title : ''
  selection.set([value.id])
  findAndSelectRealEstate({
    id: value.id,
    header: value.properties.displayName,
    subheader: subheader,
    type: 'REAL_ESTATE'
  })
}
function setRoom(value: Room) {
  selectedRoom.value = undefined
  selectedRoom.value = value
}
function findAndSelectBuilding(value: ResultObject) {
  let found = allBuildings.value.find((building) => building.id === value.id)
  if (found) {
    selection.set([found.id])
    setBuilding(found)
  }
}
function findAndSelectBuildingResult(value: ResultObject) {
  let found = allBuildings.value.find((building) => building.id === value.id)
  if (found) {
    selection.set([found.id])
    setBuilding(found)
    onSelect()
  }
}
function findAndSelectComplex(value: ResultObject) {
  let found = allComplexes.value.find((complex) => complex.id === value.id)
  if (found) {
    selection.set([found.id])
    setComplex(found)
  }
}
function findAndSelectComplexResult(value: ResultObject) {
  let found = allComplexes.value.find((complex) => complex.id === value.id)
  if (found) {
    selection.set([found.id])
    setComplex(found)
    onSelect()
  }
}
function findAndSelectRealEstate(value: ResultObject) {
  let found = allRealEstates.value.find((realEstate) => realEstate.id === value.id)
  if (found) {
    selection.set([found.id])
    setRealEstate(found)
  }
}
function findAndSelectRealEstateResult(value: ResultObject) {
  let found = allRealEstates.value.find((realEstate) => realEstate.id === value.id)
  if (found) {
    selection.set([found.id])
    setRealEstate(found)
    onSelect()
  }
}
async function findAndSelectRoom(value: ResultObject) {
  setViewToMapview()
  commitClick('region')
  const building = allBuildings.value.find((building) => building.id === value.buildingId)
  if (building) {
    setBuilding(building)
    await store.dispatch('CHANGE_BUILDING', { building: building })
  }
  const level = levelsForSelectedBuilding.value?.data.features.find(
    (level: LEVEL) => level?.properties.id === value.levelId
  )
  if (!level) return
  await store.dispatch('CHANGE_LEVEL', { levelNumber: level.properties.level })
  const room = roomsForSelectedLevel?.value.data.features.find(
    (room: Room) => room?.properties.id === value.id
  )
  if (room) {
    selection.set([room.id])
    setRoom(room)
    onSelect()
  }
}
function findParent(type: string, value: SearchObject) {
  return value.reference.parents.find((parent) => parent.type === type)
}
//  Watches
watch(searchString, (newVal, oldVal) => {
  if (searchString.value.length >= 1 && newVal !== oldVal) {
    isLoading.value = true
    fetchAndSortData(searchString.value.toString().toLocaleLowerCase())
  }
})
watch(isTyping, (newVal) => {
  if (newVal) {
    isLoading.value = true
  }
})
//  Lifecycle hooks
onMounted(() => {
  if (localStorage.getItem('recentSearches'))
    recentSearch.value = JSON.parse(localStorage['recentSearches'])
})
</script>

<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
.layout-search {
  &__accordion {
    grid-column: 2 / 12;
  }
  &__category-select {
    display: grid;
    grid-column-gap: variables.$gap-xlarge;
    grid-template-columns: repeat(4, 1fr);
    grid-column: 2 / 14;
    grid-row: 2;
    margin-bottom: variables.$margin-xxlarge;
    row-gap: calc(#{variables.$gap-xlarge} + #{variables.$gap-large});
    width: 100%;
    @media screen and (max-width: 658px) {
      row-gap: variables.$gap-xlarge;
      grid-template-columns: 1fr;
    }
  }
  &__divider {
    border-left: none;
    border-top: 1px solid variables.$color-greyscale-line-grey;
    border-bottom: none;
    border-inline: none;
    grid-column: 2 / 14;
    grid-row: 2;
    margin: 0;
    place-self: end;
    width: 100%;
  }
  &__modal {
    column-gap: inherit;
    display: grid;
    grid-template-columns: inherit;
    grid-template-rows: variables.$button-medium auto 1fr;
    @media screen and (max-width: 1024px) {
      min-height: 100%;
    }
    &::before {
      box-shadow: variables.$box-shadow-small;
      content: '';
      display: block;
      height: variables.$padding-xsmall;
      position: absolute;
      top: 0;
      width: 100%;
    }
  }
}
@media screen and (max-width: 1024px) {
  .layout-search {
    &__category-select {
      display: flex;
      flex-direction: column;
      gap: variables.$gap-xlarge;
    }
  }
}
</style>
